import { Gender } from "@prisma/client";
import { value } from "~/components/helpers";
import { type AppContext } from "~/lib/context";
import { hasGender, hasKnownGender, Measure } from "~/services/employee";
import { computeMarketCompensationData } from "~/services/market-data/dataset-employee/compute-compensations/computeMarketCompensationData";
import { type MarketDataDatasetEmployee } from "~/services/market-data/dataset-employee/getMarketDataDatasetEmployees";
import { getGenderDataAccess } from "~/services/market-data/getGenderDataAccess";
import { type ComputeMarketCompensationStatsOptions } from "~/services/marketCompensationStats";

export const computeMarketGenderEqualityStats = async (
  ctx: AppContext,
  datasetEmployees: MarketDataDatasetEmployee[],
  options: ComputeMarketCompensationStatsOptions
) => {
  const canAccessGenderData = await getGenderDataAccess(ctx);

  if (!canAccessGenderData) {
    return null;
  }

  const employeesWithGender = datasetEmployees.filter(hasKnownGender());
  const men = employeesWithGender.filter(hasGender(Gender.MALE));
  const women = employeesWithGender.filter(hasGender(Gender.FEMALE));

  const menData = await computeMarketCompensationData(ctx, men, {
    ...options,
    measures: [Measure.BaseSalary, Measure.TotalCash, Measure.OnTargetEarnings],
  });
  const womenData = await computeMarketCompensationData(ctx, women, {
    ...options,
    measures: [Measure.BaseSalary, Measure.TotalCash, Measure.OnTargetEarnings],
  });

  return {
    womenProportion: employeesWithGender.length ? women.length / employeesWithGender.length : null,
    baseSalary: value(() => {
      const menMedian = menData.data?.measures.baseSalary.median ?? null;
      const womenMedian = womenData.data?.measures.baseSalary.median ?? null;
      const payGap = menMedian && womenMedian ? (menMedian - womenMedian) / Math.max(menMedian, womenMedian) : null;

      return { menMedian, womenMedian, payGap };
    }),
    totalCash: value(() => {
      const menMedian = menData.data?.measures.totalCash.median ?? null;
      const womenMedian = womenData.data?.measures.totalCash.median ?? null;
      const payGap = menMedian && womenMedian ? (menMedian - womenMedian) / Math.max(menMedian, womenMedian) : null;

      return { menMedian, womenMedian, payGap };
    }),
    onTargetEarnings: value(() => {
      const menMedian = menData.data?.measures.onTargetEarnings.median ?? null;
      const womenMedian = womenData.data?.measures.onTargetEarnings.median ?? null;
      const payGap = menMedian && womenMedian ? (menMedian - womenMedian) / Math.max(menMedian, womenMedian) : null;

      return { menMedian, womenMedian, payGap };
    }),
  };
};
